export const rfqArticles = [
    {
        id: 'rfq.overview',
        title: 'RFQ Overview',
        category: 'RFQs',
        order: 1,
        summary: 'Request for Quote - getting pricing from suppliers',
        content: `
The RFQ (Request for Quote) page lets you request pricing from multiple suppliers for your BOQ items. This streamlines the quoting process by sending standardised requests and collecting responses in one place.

**RFQ Workflow:**

1. **Create RFQ** - Select items from a job's BOQ
2. **Add Suppliers** - Choose which suppliers to request quotes from
3. **Send** - Email the RFQ to selected suppliers with a unique response link
4. **Receive Quotes** - Suppliers submit pricing through a secure portal
5. **Compare & Apply** - Review responses, compare prices, and apply the best quotes

**Key Features:**

- Create RFQs from BOQ items
- Send to multiple suppliers simultaneously
- Suppliers respond via a secure online portal (no login required)
- Compare supplier pricing side-by-side
- Auto-apply winning prices to BOQ items
    `,
        expandableSections: [
            {
                title: 'Creating an RFQ',
                content: `
1. Go to the **RFQs** page
2. Click **New RFQ**
3. Select a **Job** and **Cost Centre**
4. Choose items to include:
   - Tick individual items, or
   - Use **Select All** to include everything
5. Add one or more **Suppliers** to quote
6. Set a **Due Date** for responses
7. Click **Create RFQ**

**Tips:**
- Group related items together for clearer supplier quotes
- Set realistic due dates (7-14 days recommended)
- Include notes for suppliers about specific requirements
        `,
            },
            {
                title: 'Sending RFQs to Suppliers',
                content: `
Once created, send the RFQ to suppliers:

1. Open the RFQ from the grid
2. Review the items and suppliers
3. Click **Send to Suppliers**
4. Each supplier receives an email with:
   - A summary of items requiring pricing
   - A unique, secure link to the response portal
   - The due date for their response

**Supplier Portal:**
Suppliers don't need an account. They click the link, see the items, enter their prices, and submit. Their response is recorded automatically.
        `,
            },
            {
                title: 'Reviewing & Applying Quotes',
                content: `
As suppliers respond:

1. The RFQ grid shows response status for each supplier
2. Open the RFQ to see all responses
3. **Compare** prices across suppliers
4. Select the best price for each item
5. Click **Apply Prices** to update the BOQ

When prices are applied:
- Supplier prices are created/updated in the catalogue
- BOQ items update to reflect the new pricing
- The supplier is recorded as the nominated supplier for those items
        `,
            },
        ],
        keywords: ['rfq', 'request for quote', 'quotation', 'supplier pricing', 'tender', 'quote'],
        route: '/rfqs',
        relatedArticles: ['rfq.supplier-portal', 'suppliers.overview', 'boq.suppliers'],
        screenshots: [
            {
                src: 'rfq/rfq-create.png',
                alt: 'New RFQ dialog',
                caption: 'Creating a request for quote — pick the job and items, then the suppliers to invite',
            },
        ],
    },
    {
        id: 'rfq.supplier-portal',
        title: 'Supplier Response Portal',
        category: 'RFQs',
        order: 2,
        summary: 'How suppliers respond to your RFQ requests',
        content: `
When you send an RFQ, each supplier receives a unique link to respond with their pricing. No account or login is required.

**What Suppliers See:**

- Your company name and details
- The list of items requiring pricing
- Item descriptions, quantities, and units
- A field to enter their unit price for each item
- Space for notes or comments

**How Suppliers Respond:**

1. Click the link in the email
2. Review the items listed
3. Enter unit prices for items they can supply
4. Leave blank any items they cannot supply
5. Add notes if needed
6. Click **Submit Response**

Responses are recorded immediately and visible in your RFQ dashboard.
    `,
        expandableSections: [
            {
                title: 'Portal Security',
                content: `
Each supplier portal link is:

- **Unique** - One link per supplier per RFQ
- **Token-secured** - Cannot be guessed
- **Tenant-isolated** - Only sees items for this specific RFQ
- **Expiry-aware** - Shows due date to encourage timely responses
- **No login required** - Suppliers don't need a Costed.app account
        `,
            },
        ],
        keywords: ['supplier portal', 'response', 'pricing', 'quote submission'],
        route: '/rfqs',
        relatedArticles: ['rfq.overview'],
    },
];
